import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { Container, Row, Col, Form, Button, Modal } from "react-bootstrap";
import loginGraphic from '../images/login-tiles.png'; 
import purpleLogo from '../images/CloudSecure_purple_logo.svg';
import logo from '../images/logo.svg';
import googleIcon from '../images/googleicon.png'; 
import facebookIcon from '../images/facebook.png'; 
import passwordView from '../images/password-view.svg'; 
import passwordHide from '../images/hidepassword.svg';
import modalLeftImage from '../images/modal-left-image.jpg'; 
import modalCloseImage from '../images/close_modal.svg'; 
import infoIconImage from '../images/info_icon.svg'; 
import { jwtDecode } from 'jwt-decode';
import { detectUserLocale } from '../utils/localeDetection';
// Shared client: tenant, client ID and API audience come from the REACT_APP_AUTH0_* env vars
import { webAuth } from '../utils/auth0';

const SignupPage = () => {
    const layersRef = useRef([]);
    const [showModal, setShowModal] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [countryCode, setCountryCode] = useState("+1");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('initial');
    const [accessToken, setAccessToken] = useState(null);
    const [userInfo, setUserInfo] = useState(null);

    useEffect(() => {
        const getRandom = (min, max) => Math.random() * (max - min) + min;
        const animateLayer = (layer, duration) => {
            gsap.fromTo(
                layer,
                {
                    x: getRandom(-400, 400),
                    y: getRandom(-400, 400),
                    scale: getRandom(1.1, 1.6),
                    opacity: getRandom(0.4, 0.7),
                    rotation: getRandom(-20, 20),
                },
                {
                    x: getRandom(-400, 400),
                    y: getRandom(-400, 400),
                    scale: getRandom(1.3, 1.8),
                    opacity: getRandom(0.6, 0.9),
                    rotation: getRandom(-20, 20),
                    duration,
                    repeat: -1,
                    yoyo: true,
                    ease: "power1.inOut",
                }
            );
        };

        layersRef.current.forEach((layer, index) => {
            const colors = [
                `radial-gradient(circle, rgba(255, 50, 150, 0.4), transparent 70%)`,
                `radial-gradient(circle, rgba(50, 150, 255, 0.4), transparent 70%)`,
                `radial-gradient(circle, rgba(150, 50, 255, 0.4), transparent 70%)`,
            ];
            layer.style.background = colors[index % colors.length];
            animateLayer(layer, getRandom(4, 6));
        });
    }, []);

    // 🔹 Detect user locale on mount and set country code
    useEffect(() => {
        const storedCurrency = localStorage.getItem('user_currency');
        const storedCountry = localStorage.getItem('user_country');
        const storedDialCode = localStorage.getItem('user_dial_code');
        
        if (!storedCurrency || !storedCountry || !storedDialCode) {
            const localeInfo = detectUserLocale();
            console.log('🌍 Detected Locale Info:', localeInfo);
            
            localStorage.setItem('user_currency', localeInfo.currency);
            localStorage.setItem('user_country', localeInfo.countryCode);
            localStorage.setItem('user_dial_code', localeInfo.dialCode);
            
            setCountryCode(localeInfo.dialCode);
        } else {
            setCountryCode(storedDialCode);
        }
    }, []);

    useEffect(() => {
        gsap.to(".img-loop", {
            x: 20,
            y: 20,
            duration: 3,
            ease: "power1.inOut",
            yoyo: true,
            repeat: -1,
        });
        gsap.to(".img-loop", {
            x: -20,
            y: -20,
            duration: 3,
            delay: 1.5,
            ease: "power1.inOut",
            yoyo: true,
            repeat: -1,
        });
    }, []);

    const handleAuthCallback = () => {
      webAuth.parseHash({ hash: window.location.hash }, (err, authResult) => {
        if (err) {
          setError(`Auth Error: ${err.errorDescription || err.error}`);
          return;
        }
        if (authResult && authResult.accessToken) {
          const decoded = jwtDecode(authResult.idToken);
          setUserInfo(decoded);
          setAccessToken(authResult.accessToken);
          localStorage.setItem('access_token', authResult.accessToken);
          localStorage.setItem('user_info', JSON.stringify(decoded));
          window.history.replaceState({}, document.title, window.location.pathname);
          setStep('authenticated');
        }
      });
    };

    useEffect(() => {
      if (window.location.hash.includes('access_token')) {
        handleAuthCallback();
      } else {
        const storedToken = localStorage.getItem('access_token');
        const storedUser = localStorage.getItem('user_info');
        if (storedToken && storedUser) {
          setAccessToken(storedToken);
          setUserInfo(JSON.parse(storedUser));
          setStep('authenticated');
        }
      }
    }, []);

    const signupWithEmailPassword = () => {
      setLoading(true);
      setError('');
      webAuth.signup({
        connection: 'Username-Password-Authentication',
        email,
        password,
        user_metadata: { name }
      }, (err) => {
        setLoading(false);
        if (err) setError(err.description || 'Signup failed');
        else loginWithEmailPassword();
      });
    };

    const loginWithEmailPassword = () => {
      setLoading(true);
      setError('');
      webAuth.login({
        realm: 'Username-Password-Authentication',
        username: email,
        password,
      }, (err) => {
        setLoading(false);
        if (err) setError(err.description || 'Login failed');
      });
    };

    const formatPhoneNumber = (phone, code = countryCode) => {
      const cleanPhone = phone.replace(/\D/g, '');
      if (phone.startsWith('+')) return phone;
      if (code === '+1') {
        if (cleanPhone.startsWith('1') && cleanPhone.length === 11) return code + cleanPhone.substring(1);
        if (cleanPhone.length === 10) return code + cleanPhone;
      } else if (code === '+91') {
        if (cleanPhone.length === 10) return code + cleanPhone;
      } else if (code === '+44') {
        if (cleanPhone.length >= 10 && cleanPhone.length <= 11) return code + cleanPhone;
      }
      return code + cleanPhone;
    };

    const sendOtp = () => {
      if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
        setError('Please enter a valid phone number (at least 10 digits)');
        return;
      }
      setLoading(true);
      setError('');
      const formattedPhone = formatPhoneNumber(phoneNumber, countryCode);
      webAuth.passwordlessStart({
        connection: 'sms',
        send: 'code',
        phoneNumber: formattedPhone,
        authParams: {
          scope: 'openid profile email phone',
          response_type: 'token id_token'
        }
      }, (err, response) => {
        setLoading(false);
        if (err) {
          let errorMessage = 'Failed to send OTP. ';
          if (err.error === 'invalid_request') errorMessage += 'Please check your phone number format.';
          else if (err.error === 'server_error') errorMessage += 'SMS provider not configured. Please contact support.';
          else if (err.error === 'bad.connection') errorMessage += 'SMS connection not properly configured in Auth0.';
          else errorMessage += err.description || err.error || 'Unknown error occurred.';
          setError(errorMessage);
        } else {
          setStep('verify-otp');
          setError('');
        }
      });
    };

    const verifyOtp = () => {
      if (!otp || otp.length !== 6) {
        setError('Please enter a valid 6-digit OTP');
        return;
      }
      setLoading(true);
      setError('');
      const formattedPhone = formatPhoneNumber(phoneNumber, countryCode);
      webAuth.passwordlessLogin({
        connection: 'sms',
        phoneNumber: formattedPhone,
        verificationCode: otp,
      }, (err) => {
        setLoading(false);
        if (err) {
          let errorMessage = 'OTP verification failed. ';
          if (err.error === 'invalid_grant') errorMessage += 'Invalid or expired verification code. Please try again.';
          else if (err.error === 'invalid_request') errorMessage += 'Invalid phone number or verification code format.';
          else if (err.description) errorMessage += err.description;
          else if (err.original?.error_description) errorMessage += err.original.error_description;
          else if (err.message) errorMessage += err.message;
          else errorMessage += 'Please check your code and try again.';
          setError(errorMessage);
        } else {
          setStep('authenticated');
        }
      });
    };

    const signupWithSocial = (provider) => {
      webAuth.authorize({
        connection: provider,
        redirectUri: window.location.origin + '/callback',
        responseType: 'token id_token',
        scope: 'openid profile email phone'
      });
    };

    const logout = () => {
      setUserInfo(null);
      setAccessToken(null);
      setStep('initial');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_info');
      setEmail('');
      setPassword('');
      setName('');
      setPhoneNumber('');
      setOtp('');
      setError('');
    };

    const resendOtp = () => {
      setOtp('');
      setError('');
      sendOtp();
    };

    if (step === 'authenticated' && userInfo) {
      return (
        <div className="p-6 bg-white shadow rounded max-w-lg w-full">
          <h2 className="text-2xl font-bold mb-4 text-green-600">✓ Signup Successful</h2>
          <div className="space-y-2 mb-4">
            <p><strong>Name:</strong> {userInfo.name || 'Not provided'}</p>
            <p><strong>Email:</strong> {userInfo.email || 'Not provided'}</p>
            {userInfo.phone_number && <p><strong>Phone:</strong> {userInfo.phone_number}</p>}
            <p><strong>User ID:</strong> {userInfo.sub}</p>
          </div>
          <button 
            onClick={logout} 
            className="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            Logout
          </button>
        </div>
      );
    }

    return (
        <>
            <Container fluid className="login-container">
            <Row className="align-items-center">
                {/* Left Section */}
                <div className="authLeft text-center d-flex align-items-center justify-content-center">
                    <img src={loginGraphic} alt="login Graphic" className="img-loop" width="70%" />
                </div>
                {/* Right Section (Signup Form) */}
                <Col className="auth-right">
                    <div className="login-form">
                        <h2 className="brand-name mb-3">
                            <img src={logo} alt="logo" height="36" />
                        </h2>
                        <h3>Create new account</h3>
                        <p className="mb-2">Securely access your account and manage your assets with ease.</p>
                        {/* Email/Password Signup */}
                        <Form className="mb-4">
                            <Form.Group controlId="fullName" className="form-floating mb-3">
                                <Form.Control type="text" placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} />
                                <Form.Label>Full Name</Form.Label>
                            </Form.Group>
                            <Form.Group controlId="email" className="form-floating mb-3">
                                <Form.Control type="email" placeholder="johnsmith@gmail.com" value={email} onChange={e => setEmail(e.target.value)} />
                                <Form.Label>Email Address</Form.Label>
                            </Form.Group>
                            <Form.Group controlId="password2" className="form-floating mb-3 position-relative password-wrapper">
                                <Form.Control type={passwordVisible ? "text" : "password"} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
                                <Form.Label>
                                    Password{" "}
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2 text-pointer-events"
                                        onClick={() => setShowModal(true)}
                                        style={{ textDecoration: "underline", cursor: "pointer" }}
                                    >
                                        <img src={infoIconImage} alt="Info" width="15" />
                                    </Button>
                                </Form.Label>
                                <span className="eye-icon" onClick={() => setPasswordVisible(!passwordVisible)}>
                                    <img
                                        src={passwordVisible ? passwordHide : passwordView}
                                        alt={passwordVisible ? "Hide password" : "Show password"}
                                        width="26"
                                    />
                                </span>
                            </Form.Group>
                            <Form.Group controlId="terms" className="mb-2">
                                <Form.Check
                                    type="checkbox"
                                    required
                                    label={<>By signing up, I agree to the <Link to="/terms" target="_blank" className="underline">Terms & Conditions</Link> of MYWORTH.AI</>}
                                />
                            </Form.Group>
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="col-md-3 px-0">
                                    <Button className="btn-login btn-block py-2" onClick={signupWithEmailPassword} disabled={loading || !email || !password || !name}>
                                        {loading ? 'Processing...' : 'Sign Up'}
                                    </Button>
                                </div>
                            </div>
                        </Form>
                        <hr className="my-4 border-gray-200" />
                        {/* Phone/OTP Signup */}
                        <div className="mb-4">
                            <Form.Label>Phone</Form.Label>
                            <Row>
                                <div className="col-3">
                                    <Form.Select className="ps-0" value={countryCode} onChange={e => setCountryCode(e.target.value)} disabled={step === 'verify-otp'}>
                                        <option value="+1">+1</option>
                                        <option value="+91">+91</option>
                                        <option value="+44">+44</option>
                                        <option value="+33">+33</option>
                                        <option value="+49">+49</option>
                                    </Form.Select>
                                </div>
                                <div className="col-9">
                                    <Form.Control type="text" placeholder="1234567890" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))} disabled={step === 'verify-otp'} />
                                </div>
                            </Row>
                            <p className="text-xs text-gray-500 mt-1">
                                Enter your phone number without country code. We'll send you a verification code.
                            </p>
                            {step === 'verify-otp' && (
                                <div>
                                    <Form.Label>Verification Code</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter 6-digit OTP"
                                        value={otp}
                                        onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        maxLength="6"
                                        className="text-center text-lg tracking-wider"
                                        autoComplete="one-time-code"
                                    />
                                    <p className="text-sm text-gray-600 mt-1">
                                        Check your phone for the 6-digit code we sent to <strong>{countryCode} {phoneNumber}</strong>
                                    </p>
                                </div>
                            )}
                            <Button
                                onClick={step === 'verify-otp' ? verifyOtp : sendOtp}
                                disabled={loading || (step === 'initial' && !phoneNumber) || (step === 'verify-otp' && otp.length !== 6)}
                                className="w-100 mt-2 btn btn-purple"
                            >
                                {loading ? 'Processing...' : (step === 'verify-otp' ? 'Verify OTP' : 'Send OTP')}
                            </Button>
                            {step === 'verify-otp' && (
                                <div className="d-flex gap-2 mt-2">
                                    <Button
                                        variant="link"
                                        onClick={() => {
                                            setStep('initial');
                                            setOtp('');
                                            setError('');
                                        }}
                                        className="flex-1 text-sm text-purple-600 hover:text-purple-800 py-2"
                                    >
                                        Change Number
                                    </Button>
                                    <Button
                                        variant="link"
                                        onClick={resendOtp}
                                        disabled={loading}
                                        className="flex-1 text-sm text-purple-600 hover:text-purple-800 py-2 disabled:text-purple-300"
                                    >
                                        {loading ? 'Sending...' : 'Resend Code'}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <hr className="my-4 border-gray-200" />
                        {/* Social Signup */}
                        <div className="social-buttons mb-3">
                            <Button variant="none" className="btn-social btn-outline-primary mb-2 w-100 d-flex align-items-center justify-content-center" onClick={() => signupWithSocial('google-oauth2')}>
                                <img src={googleIcon} alt="Google" width="32" /> Sign up with Google
                            </Button>
                            <Button variant="none" className="btn-social btn-outline-primary w-100 d-flex align-items-center justify-content-center" onClick={() => signupWithSocial('facebook')}>
                                <img src={facebookIcon} alt="Facebook" width="32" /> Sign up with Facebook
                            </Button>
                        </div>
                        {error && (
                            <div className="text-red-600 p-3 bg-red-50 border border-red-200 rounded mt-3">
                                {error}
                            </div>
                        )}
                        <p className="text-start mt-4">
                            Have an account? <Link to="/login">Log In</Link>
                        </p>
                    </div>
                </Col>
            </Row>
            {/* Password Instructions Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Body className="p-0">
                    <Button variant="none" onClick={() => setShowModal(false)} className="close_mdl">
                        <img src={modalCloseImage} alt="Close" width="22" />
                    </Button>
                    <div className="modal_wlimg">
                        <div className="modal-left-image">
                            <img src={modalLeftImage} alt="login Graphic" className="cover-img" width="100%" />
                        </div>
                        <div className="info_txt py-5 pe-4">
                            <h3 className="font_mdm">Password Format</h3>
                            <p>To keep your account secure, please follow these password guidelines:</p>
                            <ul className="ps-3">
                                <li>Must be at least 8 characters long.</li>
                                <li>Include at least one uppercase letter (A-Z).</li>
                                <li>Include at least one lowercase letter (a-z).</li>
                                <li>Include at least one number (0-9).</li>
                                <li>Use at least one special character.</li>
                            </ul>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
        </>
    );
};

export default SignupPage;
